// ─────────────────────────────────────────────────────────────────────────────
// Day 6 — Completion state machine.
//
// PURE module: no Supabase, no React, no I/O. Given an enrollment's set of
// REQUIRED lessons (with whether each is complete) and REQUIRED assignments
// (with each one's overall grade_status from Day 5), it computes whether the
// enrollment is COMPLETE, and returns a structured breakdown of what is still
// outstanding. A thin wrapper (computeCompletionForEnrollment, below) fetches
// the inputs from Supabase and feeds this pure core.
//
// COMPLETION RULE (Scott's doctrine for this build):
//   An enrollment is COMPLETE when, and only when:
//     (a) EVERY required lesson is marked complete (lesson_progress.completed_at
//         is set), AND
//     (b) EVERY required assignment has overall grade_status === 'passed'
//         (i.e. the auto portion met PASS_THRESHOLD AND all free_response items
//          were marked complete by the instructor — see src/lib/grading.ts
//          overallStatus()).
//
// "REQUIRED vs OPTIONAL": derived from the schema. A lesson is required when
// lessons.required_for_completion = true (0001_init.sql). Assignments are
// lessons of type 'workbook' | 'assignment'; a required assignment is one whose
// lesson row has required_for_completion = true. If NO lesson in the cohort is
// flagged required (e.g. the seed hasn't set the flag), the wrapper defaults to
// treating ALL lessons as required and sets `defaultedAllRequired = true` so the
// caller can surface that. This keeps the rule explicit rather than silently
// completing an enrollment that has zero requirements. See DEFAULT_ALL_REQUIRED.
//
// COMPLETION TIMESTAMP SOURCE: this module does NOT invent a timestamp. It
// reports `completedAtSource` describing where the authoritative completion time
// should come from — the LATEST of the satisfying events (the last required
// lesson's completed_at or the last required assignment's graded_at/submitted_at).
// The wrapper resolves the concrete ISO timestamp; the pure core returns the
// candidate it computed from its inputs so it stays testable without clocks.
// ─────────────────────────────────────────────────────────────────────────────

import type { GradeStatusValue } from "./types";

/**
 * When a cohort has no lesson explicitly flagged required_for_completion, the
 * wrapper treats every lesson as required (and reports defaultedAllRequired).
 * Kept as a named constant so the policy is a one-line change.
 */
export const DEFAULT_ALL_REQUIRED = true;

/** A required lesson and whether the student has completed it. */
export interface RequiredLessonState {
  lessonId: string;
  title: string | null;
  /** ISO timestamp from lesson_progress.completed_at, or null if not complete. */
  completedAt: string | null;
}

/** A required assignment and its overall Day-5 grade status. */
export interface RequiredAssignmentState {
  lessonId: string;
  title: string | null;
  /** Overall grade_status from assignment_submission_status (Day 5). */
  gradeStatus: GradeStatusValue;
  /** When the assignment reached its current status (graded_at ?? submitted_at). */
  resolvedAt: string | null;
}

/** Per-requirement outstanding breakdown. */
export interface CompletionBreakdown {
  /** Required lessons not yet complete. */
  lessonsOutstanding: RequiredLessonState[];
  /** Required assignments whose grade_status !== 'passed'. */
  assignmentsOutstanding: RequiredAssignmentState[];
  /** Total required lessons considered. */
  totalRequiredLessons: number;
  /** Total required assignments considered. */
  totalRequiredAssignments: number;
  /** Required lessons currently complete. */
  completedLessons: number;
  /** Required assignments currently passed. */
  passedAssignments: number;
}

/** Full result of the completion computation. */
export interface CompletionResult {
  complete: boolean;
  breakdown: CompletionBreakdown;
  /**
   * Candidate completion timestamp: the LATEST resolving event across all
   * required lessons + assignments, as an ISO string — or null if not complete
   * (or if no resolving timestamps were available). This is the value the
   * wrapper persists to enrollments.completed_at / completion_records.completed_at.
   */
  completedAtCandidate: string | null;
  /**
   * Human description of where the completion timestamp comes from, for docs /
   * audit (e.g. "latest of required lesson + assignment resolve times").
   */
  completedAtSource: string;
  /** 0..1 fraction of all requirements satisfied (for a progress bar). */
  progress: number;
}

/** Pick the later of two ISO timestamps (nulls ignored). */
function maxIso(a: string | null, b: string | null): string | null {
  if (!a) return b;
  if (!b) return a;
  return new Date(a).getTime() >= new Date(b).getTime() ? a : b;
}

/**
 * Compute completion from already-resolved required lesson + assignment states.
 * PURE — no I/O, no clock. The rule is: all required lessons complete AND all
 * required assignments passed.
 */
export function computeCompletion(input: {
  lessons: RequiredLessonState[];
  assignments: RequiredAssignmentState[];
}): CompletionResult {
  const { lessons, assignments } = input;

  const lessonsOutstanding = lessons.filter((l) => !l.completedAt);
  const assignmentsOutstanding = assignments.filter(
    (a) => a.gradeStatus !== "passed",
  );

  const completedLessons = lessons.length - lessonsOutstanding.length;
  const passedAssignments = assignments.length - assignmentsOutstanding.length;

  const totalRequirements = lessons.length + assignments.length;
  const satisfied = completedLessons + passedAssignments;
  const progress = totalRequirements > 0 ? satisfied / totalRequirements : 0;

  const complete =
    lessonsOutstanding.length === 0 && assignmentsOutstanding.length === 0;

  // Candidate completion time = the latest resolving event across requirements.
  let completedAtCandidate: string | null = null;
  if (complete) {
    for (const l of lessons) {
      completedAtCandidate = maxIso(completedAtCandidate, l.completedAt);
    }
    for (const a of assignments) {
      completedAtCandidate = maxIso(completedAtCandidate, a.resolvedAt);
    }
  }

  return {
    complete,
    breakdown: {
      lessonsOutstanding,
      assignmentsOutstanding,
      totalRequiredLessons: lessons.length,
      totalRequiredAssignments: assignments.length,
      completedLessons,
      passedAssignments,
    },
    completedAtCandidate,
    completedAtSource:
      "latest of required lesson completed_at and required assignment graded_at/submitted_at",
    progress,
  };
}
